<script lang="ts">
// noinspection JSUnusedGlobalSymbols

import { I3pEventsEnum } from '@nuxtDatakodeUtils/types/I3pEvents.enum'
import DkOlVectorLayer from '@nuxtDatakodeUtils/components/dk-ol-map/layers/dk-ol-vector-layer.vue'
import type { FeatureCollection } from 'geojson'
import type { FeatureLike } from 'ol/Feature'
import type { Type as GeometryType } from 'ol/geom/Geometry'
import type { ProjectionLike } from 'ol/proj'
import { inject } from 'vue'
import type olMap from 'ol/Map'
import Legend from 'ol-ext/legend/Legend'
import LegendItem from 'ol-ext/legend/Item'
import type { GeoJSONGeometry } from 'ol/format/GeoJSON'
import type { BaseModel } from '@nuxtDatakodeUtils/models/BaseModel'
import { type Extent, getCenter, getHeight, getWidth } from 'ol/extent'
import GenericLayerStyle from '~/components/I3pComponent/common/map/GenericLayerStyle.vue'
import { IntentionVectorLayerService } from '~/services/intentionVectorLayer.service'
import { IntentionMapService } from '~/services/intentionMap.service'
import { IntentionI3pService } from '~/services/intentions.i3p.service'
import type { Entity } from '~/models/Entity'
import { OpportunityMapService } from '~/services/opportunityMap.service'
import { OpportunitiesI3pService } from '~/services/opportunities.i3p.service'
import { Intention } from '~/models/Intention'
import { OpportunityEnum } from '~/enum/Opportunity.enum'

export default {
    name: 'GenericLayer',
    components: { GenericLayerStyle, DkOlVectorLayer },
    props: {
        mapService: {
            type: [IntentionMapService, OpportunityMapService, null],
            required: true,
        },
        i3pService: { type: [IntentionI3pService, OpportunitiesI3pService], required: true },
    },
    emits: ['hoverFeatures', 'clickFeatures'],
    data() {
        return {
            map: null as Map,
            geojsonData: null as FeatureCollection,
            currentEntityK: null as string,
            currentFeature: null as FeatureLike,
            editMode: false as boolean,
            dataProjection: 'EPSG:4326' as ProjectionLike,
            displayGeomAsCentroidPoint: false as boolean,
        }
    },
    computed: {
        intentionVectorLayerService(): IntentionVectorLayerService {
            return new IntentionVectorLayerService()
        },
        title(): string {
            return this.i3pService?.type === 'intention' ? this.i3pService.getLayerName() : this.i3pService.getLayerNameIntentions()
        },
        createType(): GeometryType {
            return this.i3pService.getGeometrySubtype() as GeometryType
        },
        isIntentionService(): boolean {
            return this.i3pService?.type === 'intention'
        },
        isOpportunityService(): boolean {
            return this.i3pService?.type === 'opportunity'
        },
        isDisplayAll(): boolean {
            return this.i3pService.getNavigationMode() === 'displayAll'
        },
        create(): boolean {
            return this.editMode && this.i3pService.isCreation()
        },
    },
    mounted() {
        this.map = inject('map') as olMap
        this.dataProjection = this.i3pService.getDataProjection()
        if (this.isIntentionService) {
            this.i3pService.emitter
                .on(I3pEventsEnum.mapFocusBbox, (event: {
                    bbox: {
                        x_min: number
                        x_max: number
                        y_min: number
                        y_max: number
                    }
                }) => {
                    this.setMapBoundaries(event.bbox)
                })

            this.i3pService.emitter
                .on(I3pEventsEnum.geojsonUpdated, (event: { geojsonData: FeatureCollection }) => {
                    this.geojsonData = event.geojsonData
                })

            this.i3pService.emitter
                .on(I3pEventsEnum.switchEditMode, (event: { editMode: boolean }) => {
                    this.editMode = event.editMode
                    if (this.create) {
                        this.refreshCurrenEntityK(null)
                    }
                    if (!this.editMode) {
                        this.$refs.intentionLayer?.resetCoordinates()
                    }
                })

            this.i3pService.emitter
                .on(I3pEventsEnum.beforeSaveEntity, () => {
                    // to avoid reset geom during save
                    this.$refs.intentionLayer?.saveOriginalCoordinates()
                })

            // change current entity
            this.i3pService.emitter
                .on(I3pEventsEnum.currentEntityUpdating, (event: { currentEntityK: string | number }) => {
                    this.refreshCurrenEntityK(event.currentEntityK)
                })

            // current entity updated
            this.i3pService.emitter
                .on(I3pEventsEnum.currentEntityUpdated, (event: { currentEntity: Entity }) => {
                    this.updateCurrenFeatureStatus(event.currentEntity)
                })

            // entity updated or created
            for (const event of [I3pEventsEnum.entityUpdated, I3pEventsEnum.entityCreated]) {
                this.i3pService.emitter
                    .on(event, (event: { entity: Entity }) => {
                        this.refreshCurrenEntityK(event.entity?.getKey())
                    })
            }

            // entity deleted
            this.i3pService.emitter
                .on(I3pEventsEnum.entityDeleted, () => {
                    this.refreshCurrenEntityK(null)
                })
        }
        else if (this.isOpportunityService) {
            this.i3pService.emitter
                .on(I3pEventsEnum.currentEntityUpdated, (event: {
                    currentEntity: null | BaseModel<OpportunityData>
                }) => {
                    if (event.currentEntity) {
                        this.intentionsGeojson = null
                        this.loadIntentionsGeojson(event.currentEntity.data.k)
                    }
                })

            this.i3pService.emitter
                .on(OpportunityEnum.changeCurrentIntentionK, (event: { currentIntentionK: string }) => {
                    const k = event.currentIntentionK
                    const hasChanged = k !== this.currentEntityK
                    if (hasChanged && k) {
                        this.refreshCurrenEntityK(k)
                    }
                    else {
                        this.refreshCurrenEntityK(null)
                    }
                })
        }
    },
    methods: {
        updateLegendVectorStyle() {
            const legend: Legend = this.mapService?.legend as Legend
            const intentionsLayer = this.$refs?.intentionLayer?.layer
            if (!legend || !intentionsLayer)
                return

            // 1) Remove ALL existing top-level "Inventaires" groups (if any)
            const topItems = legend.getItems?.()?.getArray() ?? []

            if (topItems && topItems.length > 0) {
                topItems
                    .filter((it: any) => {
                        return (it.getTitle() === intentionsLayer.get('title'))
                    })
                    .forEach((it: any) => legend.removeItem(it))
            }
            const source = intentionsLayer.getSource()
            if (!source)
                return
            const features = source.getFeatures()
            if (!features)
                return

            const styleMap: Record<string, { title: string, style: any }> = {}

            const sortedFeatures = (features as FeatureLike[]).sort((a, b) =>
                a.get('due_status') > b.get('due_status') ? 1 : -1,
            )

            sortedFeatures.forEach((feature: FeatureLike) => {
                const title = feature.get('due_status')
                if (!styleMap[title]) {
                    styleMap[title] = {
                        title: this.$translate(`voirie.intentions.due_status.${title}`),
                        style: this.intentionVectorLayerService.getLineStringStyle(feature, false, false),
                    }
                }
            })

            const intentionsLegend = new Legend({
                title: intentionsLayer.get('title'),
                layer: intentionsLayer,
            })
            legend.addItem(intentionsLegend)

            Object.keys(styleMap)
                .sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }))
                .forEach((key: string) => {
                    const legendItem = new LegendItem({
                        title: styleMap[key].title,
                        typeGeom: 'MultiLineString',
                        style: styleMap[key].style,
                    })
                    intentionsLegend.addItem(legendItem)
                })
        },
        refreshCurrenEntityK(k: string | number | null) {
            const hasChanged = k !== this.currentEntityK
            this.currentEntityK = k
            this.currentFeature = k ? this.$refs.intentionLayer?.getFeatureByKey(k) : null
            if (this.currentFeature) {
                const features = this.$refs.intentionLayer?.getFeaturesByKey(k)
                this.intentionVectorLayerService.setSelectedFeatures(features)
            }
            else {
                this.intentionVectorLayerService.setSelectedFeatures(null)
            }

            if (hasChanged && k) {
                this.centerOnCurrentEntity()
            }
            this.updateLegendVectorStyle()
        },
        updateCurrenFeatureStatus(entity: Entity) {
            const feature = this.currentFeature
            const data = entity?.data
            const key = 'status'
            if (feature && data && feature[key] !== data[key] && data[key]) {
                this.currentFeature.set('status', data[key])
            }
        },
        centerOnCurrentEntity(): void {
            if (!this.currentFeature) {
                return
            }
            switch (this.i3pService.getNavigationMode()) {
                case 'zoomOnItem':
                    this.$refs.intentionLayer?.centerOnFeature(this.currentFeature, true, 18)
                    break
                case 'displayAll':
                    this.$refs.intentionLayer?.centerOnFeatureIfOutOfScope(this.currentFeature)
                    break
            }
        },
        displayAll(maxZoom: number = 17): void {
            if (this.isDisplayAll)
                this.$refs.intentionLayer?.displayAll(maxZoom)
        },

        hoverFeatures(features: FeatureLike[]) {
            this.$emit('hoverFeatures', { features })
        },

        clickFeatures(features: FeatureLike[]) {
            this.$emit('clickFeatures', { features })
        },
        updateGeometry(event: { geometry: SimpleGeometry | null, reprojectedGeometry: GeoJSONGeometry | null }) {
            if (!event.geometry) {
                this.i3pService.triggerGeometryChanged(null)
                return
            }

            // transform geometry to multiple
            const geometry = this.intentionVectorLayerService.forceMultiGeoJSONGeometry(event.reprojectedGeometry as GeoJSONGeometry)
            console.warn(`update Geom: ${JSON.stringify(geometry)}`)
            this.i3pService.triggerGeometryChanged(event.reprojectedGeometry)
        },
        // For Opportunities
        async loadIntentionsGeojson(currentEntityK): void {
            const model = new Intention()
            this.geojsonData = await model.getGeojson(
                { display_all_organizations: 'true', opportunity_id: currentEntityK },
            )
        },
        clickIntentionFeatures(features: FeatureLike[]) {
            this.intentionVectorLayerService.setSelectedFeatures(features)
        },

        setMapBoundaries(extent: Extent) {
            const view = this.map.getView()
            const width = getWidth(extent)
            const height = getHeight(extent)

            if (width < 20 && height < 20) {
                // Very small feature → just center with a fixed zoom
                const center = getCenter(extent)
                view.animate({
                    center,
                    zoom: 17, // or whatever feels right for "street level"
                    duration: 1000,
                })
            }
            else {
                // Bigger feature → fit with padding
                view.fit(extent, {
                    duration: 1000,
                    minResolution: 2, // prevents insane zoom
                    padding: [100, 100, 100, 100], // pixels
                })
            }
        },
    },

}
</script>

<template>
    <template v-if="geojsonData && isIntentionService">
        <DkOlVectorLayer
            ref="intentionLayer"
            :geojson-data
            :title
            :vector-layer-service="intentionVectorLayerService"
            :selected-features="(currentFeature && !create) ? [currentFeature] : []"
            :edit-selected="i3pService.editMode"
            :create
            :display-geom-as-centroid-point
            :create-type="createType"
            :allow-multiple-geometry="true"
            @hover-features="hoverFeatures($event)"
            @click-features="clickFeatures($event)"
            @update-geometry="updateGeometry($event)"
            @load-json-done="displayAll(null)"
        >
            <GenericLayerStyle
                ref="layerStyle"
                :vector-layer-service="intentionVectorLayerService"
            />
            <template #edition-layer>
                <GenericLayerStyle
                    :vector-layer-service="intentionVectorLayerService"
                    :is-edit="true"
                />
            </template>
        </DkOlVectorLayer>
    </template>
    <template v-else-if="geojsonData && isOpportunityService">
        <DkOlVectorLayer
            ref="intentionLayer"
            :geojson-data
            :title
            :vector-layer-service="intentionVectorLayerService"
            @click-features="clickIntentionFeatures($event)"
        >
            <GenericLayerStyle
                ref="layerStyle"
                :vector-layer-service="intentionVectorLayerService"
            />
        </DkOlVectorLayer>
    </template>
</template>
